<template>
  <div
          class="relative border-gray-300 rounded-md border border-solid"
          :style="graphHeight">
      <div    class="absolute w-full z-10"
              id="chartdiv"
              ref="chartdiv"
              :style="graphHeight"
      ></div>
  </div>
  <sonicu-header v-if="companiesWithoutCoordinates.length > 0">Companies without Coordinates</sonicu-header>
  <div  v-if="companiesWithoutCoordinates.length > 0"
      class="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <a v-for="key in companiesWithoutCoordinates"
         :href="'/' + key"
         :key="'nocoord' + key">
          <div
            class="relative flex items-center space-x-3 rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:border-gray-400">
              <div class="flex-shrink-0">
                  <div class="h-10 w-10 rounded-full" :style="'background-color: ' + updateCircleColor(companyColorValues[key])"></div>
              </div>
              <div class="min-w-0 flex-1">
                  <span class="absolute inset-0" aria-hidden="true" />
                  <p class="text-sm font-medium text-gray-900">{{ companies[key]['name'] }}</p>
                  <p class="truncate text-sm text-gray-500">{{ companies[key]['id'] }}</p>
              </div>
          </div>
      </a>
  </div>


</template>

<script>
import * as am5 from "@amcharts/amcharts5";
import * as am5map from "@amcharts/amcharts5/map";
import {amchartsMixin} from "@/mixins/amchartsMixin";
import am5geodata_usaLow from "@amcharts/amcharts5-geodata/usaLow";
import axios from "axios";
import SonicuHeader from "@/components/elements/SonicuHeader.vue";

// Company summaries are pulled in batches through a bounded worker pool so a
// map of hundreds of companies fires ceil(N / BATCH_SIZE) requests (CONCURRENCY
// at a time) instead of one per company. Mirrors the Enterprise Alarm Report
// loader. The batch endpoint caps at 25 companies per request.
const BATCH_SIZE = 10
const CONCURRENCY = 2

export default {
    name: "EnterpriseDashboardMap",
    components: {SonicuHeader},
    mixins: [amchartsMixin],
    props: {
        companies: Object,
        companyId: String,
    },
    data() {
        return {
            companiesWithoutCoordinates: [],
            companyColorValues: {},
            companyStatusMap: {},
        }
    },
    // Store amCharts objects outside reactive data
    chart: null,
    pointSeries: null,
    chartData: [],
    mounted(){
        this.initializeAM5Charts();
        let root = am5.Root.new("chartdiv");

        this.$options.chart = root.container.children.push(am5map.MapChart.new(root, {
            panX: "rotateX",
            projection: am5map.geoAlbersUsa(),
        }));

        // // Create a polygon series for the map
        let polygonSeries = this.$options.chart.series.push(am5map.MapPolygonSeries.new(
          root, {
              geoJSON: am5geodata_usaLow,
              fill: am5.color("#93c5fd"), //blue-300
          }
        ));

        let pointSeries = this.$options.chart.series.push(
          am5map.ClusteredPointSeries.new(root, {
              minDistance: 30,
              scatterDistance: 30,
              scatterRadius: 12
          })
        );

        let self = this;

        pointSeries.set("clusteredBullet", function(root, series, dataItem) {
            let container = am5.Container.new(root, {
                cursorOverStyle: "pointer"
            });

            let clusterColor = am5.color("#a3a3a3");
            if (dataItem && dataItem.get("children")) {
                let worstStatus = 0;
                dataItem.get("children").forEach(child => {
                    let status = child.dataContext.status || 0;
                    if (status > worstStatus) worstStatus = status;
                });
                clusterColor = self.getStatusColor(worstStatus);
            }

            container.children.push(am5.Circle.new(root, {
                radius: 20,
                fill: clusterColor,
                stroke: am5.color(0xFFFFFF),
                strokeWidth: 2,
                tooltipText: "{value} sites",
                dynamic: true,
            }));

            container.children.push(am5.Label.new(root, {
                centerX: am5.p50,
                centerY: am5.p50,
                fill: am5.color("#ffffff"),
                populateText: true,
                fontSize: 14,
                fontWeight: "600",
                text: "{value}"
            }));

            container.events.on("click", function(e) {
                pointSeries.zoomToCluster(e.target.dataItem);
            });

            return am5.Bullet.new(root, {
                sprite: container
            });
        });

        pointSeries.bullets.push(function(root, series, dataItem) {
            console.log("dataContext", dataItem.dataContext);

            let thisCircle = am5.Circle.new(root, {
                radius: 10,
                fill: am5.color("#a3a3a3"),
                stroke: am5.color(0xFFFFFF),
                strokeWidth: 2,
                dynamic: true,
                tooltipText: "{title}",
                cursorOverStyle: "pointer"
            });

            thisCircle.events.on("click", function(ev){
                console.log("clicked on", ev.target.dataItem);
                let companyId = ev.target.dataItem.dataContext.companyId;
                if (companyId) {
                    window.location.href = "/" + companyId;
                }
            });

            return am5.Bullet.new(root, {
                sprite: thisCircle,
                dynamic: true,
            })
        });

        this.$options.pointSeries = pointSeries;

        this.setGraphDataFromProps(pointSeries);
    },
    methods: {
        setGraphDataFromProps(){
            this.$options.chartData = []
            const summaryQueue = []
            // Classify companies locally (no request needed) and collect every
            // company id to pull a status summary for in batches.
            for (const [key, value] of Object.entries(this.companies)) {
                if(this.companies[key].latitude === null || this.companies[key].longitude === null){
                    this.companiesWithoutCoordinates.push(key);
                    // TODO: We should add a notice when sites are accessible but don't have lat/long
                } else {
                    this.$options.chartData.push({
                        geometry: {
                            type: "Point",
                            coordinates: [this.companies[key].longitude, this.companies[key].latitude]
                        },
                        title: this.companies[key].name,
                        companyId: key,
                        status: 0,
                    })
                }
                summaryQueue.push(key);
            }
            this.$options.pointSeries.data.setAll(this.$options.chartData);
            this.loadCompanySummaries(summaryQueue);
        },
        // Pull mapStatus for every company through a bounded worker pool: chunk
        // the ids, then run CONCURRENCY workers pulling batches off a shared
        // queue. Each batch colors its bullets / no-coordinate cards as it lands.
        async loadCompanySummaries(ids){
            // {company_id} segment is only for routing/middleware; the controller
            // authorizes each child id itself. Fall back to the sonicu root.
            const root = this.companyId || 'sonicu'
            const batches = []
            for (let i = 0; i < ids.length; i += BATCH_SIZE) {
                batches.push(ids.slice(i, i + BATCH_SIZE))
            }

            const loadBatch = async (batch) => {
                try {
                    const response = await axios.get(
                        `/api-internal/${root}/dashboard/company-summary-batch?companies=${batch.join(',')}`
                    )
                    const summaries = response.data.summaries || {}
                    Object.keys(summaries).forEach(key => {
                        const summaryValue = summaries[key].mapStatus;
                        this.companyColorValues[key] = summaryValue;
                        this.companyStatusMap[key] = summaryValue;
                        this.updateBulletColor(key, summaryValue);
                    })
                } catch (error) {
                    console.error(error);
                }
            }

            const queue = batches.slice()
            const worker = async () => {
                for (let batch = queue.shift(); batch; batch = queue.shift()) {
                    await loadBatch(batch)
                }
            }
            await Promise.all(Array.from({ length: Math.min(CONCURRENCY, batches.length) }, worker))
        },
        updateBulletColor(companyId, statusInteger){
            let thisColor = this.getStatusColor(statusInteger);

            for (var i = 0; i < this.$options.chartData.length; i++) {
                if(this.$options.chartData[i].companyId === companyId){
                    this.$options.chartData[i].status = statusInteger;
                }
            }

            for (var i = 0; i < this.$options.pointSeries.dataItems.length; i++) {
                let dataItem = this.$options.pointSeries.dataItems[i];
                if(dataItem.dataContext.companyId === companyId){
                    console.log('Updating data item for ', companyId, 'to', statusInteger, 'with color', thisColor);
                    dataItem.dataContext.status = statusInteger;
                    if (dataItem.bullets && dataItem.bullets.length > 0) {
                        let sprite = dataItem.bullets[0].get('sprite');
                        sprite.set('fill', thisColor);
                    }
                }
            }

            this.updateClusterColors();
        },
        updateClusterColors(){
            if (this.$options.pointSeries && this.$options.pointSeries.clusteredDataItems) {
                for (var i = 0; i < this.$options.pointSeries.clusteredDataItems.length; i++) {
                    let clusterDataItem = this.$options.pointSeries.clusteredDataItems[i];
                    let children = clusterDataItem.get("children");

                    if (children && children.length > 0) {
                        let worstStatus = 0;
                        children.forEach(child => {
                            let status = child.dataContext.status || 0;
                            if (status > worstStatus) worstStatus = status;
                        });

                        let color = this.getStatusColor(worstStatus);
                        let bullet = clusterDataItem.get("bullet");

                        if (bullet) {
                            let container = bullet.get('sprite');
                            if (container && container.children && container.children.length > 0) {
                                let circle = container.children.getIndex(0);
                                if (circle) {
                                    circle.set('fill', color);
                                }
                            }
                        }
                    }
                }
            }
        },
        getStatusColor(statusInteger){
            if(statusInteger === 0){
                return am5.Color.fromString("#a3a3a3");
            } else if(statusInteger === 1){
                return am5.Color.fromString("#4ade80");
            } else if(statusInteger === 2){
                return am5.Color.fromString("#facc15");
            } else if (statusInteger === 3) {
                return am5.Color.fromString("#f87171");
            } else if (statusInteger === 4) {
                return am5.Color.fromString("#22d3ee");
            } else {
                return am5.Color.fromString("#9ca3af");
            }
        },
        updateCircleColor(statusInteger){
            let thisColor;
            if(statusInteger === 0){
                thisColor = "#a3a3a3";
            } else if(statusInteger === 1){
                thisColor = "#4ade80";
            } else if(statusInteger === 2){
                thisColor = "#facc15";
            } else if (statusInteger === 3) {
                thisColor = "#f87171";
            } else if (statusInteger === 4) {
                thisColor = "#22d3ee";
            } else {
                thisColor = "#9ca3af";
            }
            return thisColor;
        },
    },
    computed: {
        graphHeight(){
            let clientHeight = document.documentElement.clientHeight;
            clientHeight = Math.max(clientHeight - 400, 450);
            return "height: " + clientHeight.toString() + "px;"
        },
    },
}
</script>

<style scoped>

</style>